import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

export const useUrlState = (config) => {
  const history = useHistory();
  const location = useLocation();

  const state = useMemo(() => {
    const params = new URLSearchParams(location.search);

    return Object.fromEntries(
      Object.entries(config).map(([key, param]) => {
        const value = params.get(key);
        return [key, value === null ? param.defaultValue : param.parse(value)];
      })
    );
  }, [config, location.search]);

  const setState = useCallback(
    (partialState, { replace = false } = {}) => {
      const params = new URLSearchParams(location.search);

      Object.entries(partialState).forEach(([key, value]) => {
        if (!Object.prototype.hasOwnProperty.call(config, key)) return;

        const param = config[key];
        const serialized = param.serialize(value);

        if (
          value === undefined ||
          value === null ||
          value === param.defaultValue ||
          serialized === undefined
        ) {
          params.delete(key);
        } else {
          params.set(key, serialized);
        }
      });

      const search = params.toString();
      const currentSearch = location.search.replace(/^\?/, '');
      if (search === currentSearch) return;

      const nextLocation = {
        pathname: location.pathname,
        search: search ? `?${search}` : '',
        hash: location.hash,
        state: location.state
      };

      if (replace) history.replace(nextLocation);
      else history.push(nextLocation);
    },
    [config, history, location]
  );

  return [state, setState];
};
